import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { EARTH_LAYER } from 'foundations-components/transitional/constants';
import { Button } from 'foundations-components/transitional/components/Button';
import { useButtonUpgradePath } from '../hooks/useButtonUpgradePath';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import emptyFunction from 'react-utils/emptyFunction';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { View } from 'foundations-components/components/View';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import { LockedIcon } from 'foundations-assets/icons/components/LockedIcon';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { WarningIcon } from 'foundations-assets/icons/components/WarningIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledCircleIconWrapper = styled.div.withConfig({
  displayName: "OverflowActivityOptionLabel__StyledCircleIconWrapper",
  componentId: "sc-1wqutdw-0"
})(["position:absolute;top:8px;left:4px;z-index:", ";font-size:8px;"], EARTH_LAYER);
const StyledButtonWrapper = styled.div.withConfig({
  displayName: "OverflowActivityOptionLabel__StyledButtonWrapper",
  componentId: "sc-1wqutdw-1"
})(["position:relative;&:hover{background:", ";}button{width:100%;cursor:", ";}"], ({
  theme
}) => theme.color['fill-field-hover'], ({
  isDisabled
}) => isDisabled ? 'not-allowed' : 'pointer');
function OverflowActivityOptionLabel({
  handleClick,
  IconComponent,
  interactionName,
  isDisabled,
  isError = false,
  label,
  showCarrot,
  tooltip
}) {
  const buttonUpgradePoint = useButtonUpgradePath(interactionName);
  const {
    openUpgradeModal
  } = useUpgradeModal(buttonUpgradePoint && buttonUpgradePoint.upgradeKey || '');
  const handleOpenUpgradeModel = useCallback(() => {
    openUpgradeModal();
  }, [openUpgradeModal]);
  const renderedErrorIcon = useMemo(() => isError &&
  /*#__PURE__*/
  /* @bend-expect trellis-tokenized */
  _jsx(StyledCircleIconWrapper, {
    children: /*#__PURE__*/_jsx(IconCircle, {
      legacy: true,
      size: "inherit",
      icon: WarningIcon,
      backgroundColor: "icon-alert-default",
      color: "icon-core-on-fill-default",
      borderColor: "icon-alert-default"
    })
  }), [isError]);
  const renderedOption = useMemo(() => /*#__PURE__*/_jsx(Tooltip, {
    title: tooltip,
    disabled: !isDisabled,
    placement: "right",
    maxInlineSize: "240px",
    children: /*#__PURE__*/_jsx(StyledButtonWrapper, {
      isDisabled: isDisabled,
      children: /*#__PURE__*/_jsx(Button, {
        use: "unstyled",
        paddingInline: "500",
        paddingBlock: "200",
        "data-selenium-test": `overflow-activity-button-${interactionName}`,
        "data-unit-test": interactionName,
        onClick: buttonUpgradePoint && !buttonUpgradePoint.hasScope ? handleOpenUpgradeModel : !isDisabled ? handleClick : emptyFunction,
        disabled: isDisabled,
        children: /*#__PURE__*/_jsxs(TFlex, {
          alignItems: "center",
          justifyContent: "space-between",
          children: [/*#__PURE__*/_jsxs(TFlex, {
            alignItems: "end",
            children: [renderedErrorIcon, /*#__PURE__*/_jsx(IconComponent, {
              color: isDisabled ? 'icon-core-disabled' : 'icon-core-default',
              size: "500"
            }), /*#__PURE__*/_jsx(View, {
              as: "span",
              paddingInlineStart: "400",
              children: label
            }), buttonUpgradePoint && !buttonUpgradePoint.hasScope ? /*#__PURE__*/_jsx(View, {
              as: "span",
              paddingInlineStart: "100",
              maxBlockSize: "22px",
              children: /*#__PURE__*/_jsx(LockedIcon, {
                size: "300",
                color: isDisabled ? 'icon-core-disabled' : 'icon-core-default'
              })
            }) : null]
          }), showCarrot ? /*#__PURE__*/_jsx(RightIcon, {
            size: "300",
            color: isDisabled ? 'icon-core-disabled' : 'icon-core-default'
          }) : null]
        })
      })
    })
  }), [buttonUpgradePoint, handleClick, handleOpenUpgradeModel, IconComponent, interactionName, isDisabled, label, renderedErrorIcon, showCarrot, tooltip]);
  return renderedOption;
}
export default OverflowActivityOptionLabel;